import { configure } from 'safe-stable-stringify';
import { organizationData } from '@/store/helpers';
import { computed, ComputedRef, ref } from 'vue';
import {
  ItemsCacheKeys,
  PreparedWorkItem,
  ResponseAggregationValues,
} from '@/models/item.models';
import { useItemStore } from '@/store/itemStore';
import { isPlainObject, uniq } from 'lodash-es';
import {
  calculatedEstimate,
  calculatedEstimateSd,
  estimateString,
} from './estimate';
import { useOrganizationStore } from '@/store/organizationStore';
import { EstimateOptionConfidence } from '@/api';
import { Board, BoardFolder, Cadence } from '@/api';
import { useBoardStore } from '@/store/boardStore';
import { LocalStorageKeys, orgScopedKey } from './local-storage';
import { useLanes } from './team';
import { useStorageWithComputedKey } from '@/utils/helpers';
import { useMasterSearchRecents } from '@/components/navigation/kbd-search/utils';
import { FindItemsData, IntegrationProvider } from '@/api';
import { Lane, ItemType } from '@/api';

const stringify = configure({ deterministic: true });
const getBoardItemsCursorKey = (
  board: Board,
  filters?: FindItemsData['query']
) => {
  if (!filters) return board.id;
  return board.id + '-' + stringify(filters);
};

export function useFetchBoards() {
  const { organization } = organizationData();
  const itemStore = useItemStore();

  // items
  const boardItemsLoading = ref(false);
  const boardItemsCursors: Record<string, string> = {};
  const boardItemsGrandTotals = ref<Record<string, number>>({});
  const boardAggregations = ref<Record<string, ResponseAggregationValues>>({});

  /**
   * Check if all items have been loaded for the passed board + filters (optional).
   */
  function allBoardItemsLoaded(board: Board, query?: FindItemsData['query']) {
    const cursorKey = getBoardItemsCursorKey(board, query);
    return cursorKey in boardItemsCursors && !boardItemsCursors[cursorKey];
  }
  /**
   * Check if some (initial) items have been loaded for the passed board + filters (optional).
   */
  function someBoardItemsLoaded(board: Board, query?: FindItemsData['query']) {
    return !!boardItemsCursors[getBoardItemsCursorKey(board, query)];
  }

  /**
   * Incrementally loads board items with each subsequent call
   */
  async function loadBoardItems(
    board: Board,
    key: ItemsCacheKeys,
    query?: FindItemsData['query']
  ) {
    if (!organization.value) return;
    if (
      allBoardItemsLoaded(board) ||
      (query && allBoardItemsLoaded(board, query))
    ) {
      const cursorKey = getBoardItemsCursorKey(board, query);
      return boardAggregations.value[cursorKey] || {};
    }

    const cursorKey = getBoardItemsCursorKey(board, query);
    const currentCursor = boardItemsCursors[cursorKey];
    boardItemsCursors[cursorKey] = ''; // prevent multiple calls
    boardItemsLoading.value = true;
    const response = await itemStore.loadBoardWorkItems({
      boardId: board.id,
      key,
      query: {
        cursor: currentCursor,
        limit: 200,
        ...(query || {}),
      },
    });
    boardItemsCursors[cursorKey] = response?.meta.cursor || '';
    boardAggregations.value[cursorKey] = response?.meta.aggregations || {};
    boardItemsGrandTotals.value[cursorKey] = response?.meta.total || 0;
    boardItemsLoading.value = false;
    return response?.meta.aggregations || {};
  }

  async function loadBoardAggregations(
    board: Board,
    query: FindItemsData['query'] & { extraAggregations?: string[] }
  ) {
    const cursorKey = getBoardItemsCursorKey(board, query);
    const defaultAggregations = [
      'assigneeCount',
      'estimate',
      'estimateSD',
      'itemCount',
      'pointEffort',
      'timeSpentSeconds',
      'assigneeUsernames',
      'parentNumbers',
      'workTypeIds',
      'color',
      'priorityIds',
      'laneIds',
    ] as string[];

    const baseAggregations: string[] =
      (query?.aggregations as string[] | undefined) || defaultAggregations;
    const finalAggregations = query?.extraAggregations
      ? [...baseAggregations, ...query.extraAggregations]
      : baseAggregations;

    const { response } = await itemStore.search({
      query: {
        ...query,
        aggregations: finalAggregations,
        limit: 1,
        itemType: ItemType.WORKITEM,
        boardId: [board.id],
      },
    });
    boardItemsGrandTotals.value[cursorKey] =
      response?.meta?.total ?? boardItemsGrandTotals.value[cursorKey] ?? 0;
    boardAggregations.value[cursorKey] = response?.meta?.aggregations || {};

    return boardAggregations.value[cursorKey];
  }

  const boardItemsMeta = computed(() => {
    const totals = boardItemsGrandTotals.value; // access here to create reactive dependency
    const aggregations = boardAggregations.value;

    return (board: Board, query?: FindItemsData['query']) => {
      const cursorKey = getBoardItemsCursorKey(board, query);
      return {
        grandTotal: totals[cursorKey] || 0,
        cursor: boardItemsCursors[cursorKey],
        aggregations: aggregations[cursorKey],
      };
    };
  });

  return {
    boardItemsMeta,
    loadBoardAggregations,
    boardItemsLoading,
    loadBoardItems,
    loadMoreBoardItems: loadBoardItems,
    allBoardItemsLoaded,
    someBoardItemsLoaded,
  };
}

export function useBoardTotals({
  allItemsLoaded,
  filters,
  items,
  aggregationsCollection,
}: {
  allItemsLoaded: ComputedRef<boolean>;
  filters: ComputedRef<FindItemsData['query'] | undefined>;
  items: ComputedRef<PreparedWorkItem[]>;
  aggregationsCollection?: ComputedRef<
    Array<Record<string, number | string[]>>
  >;
}) {
  const showTotalsBasedOnAssignee = computed(() => {
    if (!allItemsLoaded.value) return false;
    const appliedAssigneeFilter = filters.value?.assigneeUsername;
    if (!appliedAssigneeFilter?.length || !filters.value?.assigneeSubItems)
      return false;
    // check if all the items are assigned to users in the assignee filter
    const allItemsHaveBaseAssignee = items.value.every((item) => {
      return (
        !!item.assigneeUsername &&
        appliedAssigneeFilter.includes(item.assigneeUsername)
      );
    });
    return !allItemsHaveBaseAssignee;
  });

  function getAggregationCountBasedOnAttr(
    aggregationObjects: Array<Record<string, number | string[]>>,
    attr: string
  ) {
    const counts = aggregationObjects
      .map((aggregation) => {
        return aggregation?.[attr];
      })
      .filter((i) => !!i && typeof i === 'number') as number[];
    const totCount = counts.reduce((acc, count) => {
      return acc + count;
    }, 0);
    return totCount;
  }

  function getAggregatedCountBasedOnAssigneeFilter(
    attr: 'estimate' | 'pointEffort' | 'timeSpentSeconds' | 'estimateSd',
    assignees: string[]
  ) {
    const itemsWithMainAssignees = items.value.filter((item) => {
      return (
        !!item.assigneeUsername && assignees.includes(item.assigneeUsername)
      );
    });
    const totals = [
      itemsWithMainAssignees.reduce((acc, item) => {
        return acc + (item[attr] || 0);
      }, 0) || 0,
    ];
    const otherItems = items.value.filter((item) => {
      return (
        !item.assigneeUsername || !assignees.includes(item.assigneeUsername)
      );
    });
    const subItems: PreparedWorkItem['subItems'] = [];
    for (const item of otherItems) {
      if (item.subItems) {
        subItems.push(
          ...item.subItems.filter(
            (i) =>
              !!i.assigneeUsername && assignees.includes(i.assigneeUsername)
          )
        );
      }
    }

    let subItemsTotal = 0;
    const organizationStore = useOrganizationStore();
    const defaultEstimateOptionCollection =
      organizationStore.activeOrganization?.estimateOptionCollections?.find(
        (opt) => opt.default
      );
    const confidenceLevel =
      defaultEstimateOptionCollection?.confidence ||
      EstimateOptionConfidence._68;

    for (const item of subItems) {
      if (attr === 'pointEffort' || attr === 'timeSpentSeconds') {
        subItemsTotal += item[attr] || 0;
      } else if (attr === 'estimate') {
        // SubItemSimple doesn't have estimate - compute from individual estimates
        subItemsTotal +=
          calculatedEstimate(
            item.optimisticEstimate,
            item.likelyEstimate,
            item.pessimisticEstimate
          ) || 0;
      } else if (attr === 'estimateSd') {
        // SubItemSimple doesn't have estimateSd - compute from individual estimates
        subItemsTotal +=
          calculatedEstimateSd(
            item.optimisticEstimate,
            item.pessimisticEstimate,
            confidenceLevel
          ) || 0;
      }
    }
    totals.push(subItemsTotal || 0);
    return totals.reduce((acc, total) => {
      return acc + total;
    }, 0);
  }

  const totalItemCount = computed(() => {
    if (aggregationsCollection?.value) {
      return getAggregationCountBasedOnAttr(
        aggregationsCollection.value,
        'itemCount'
      );
    } else {
      return items.value.length;
    }
  });

  const totalMemberCount = computed(() => {
    if (showTotalsBasedOnAssignee.value) {
      return filters.value?.assigneeUsername?.length || 0;
    }
    if (aggregationsCollection?.value) {
      return getAggregationCountBasedOnAttr(
        aggregationsCollection.value,
        'assigneeCount'
      );
    }
    const assigneeIds = items.value
      .map((i) => i.assigneeUsername)
      .filter((i) => !!i);
    return uniq(assigneeIds).length;
  });

  const totalTimeEstimate = computed(() => {
    let estimateSum = 0;
    if (showTotalsBasedOnAssignee.value) {
      estimateSum = getAggregatedCountBasedOnAssigneeFilter(
        'estimate',
        filters.value?.assigneeUsername || []
      );
    } else if (aggregationsCollection?.value) {
      estimateSum = getAggregationCountBasedOnAttr(
        aggregationsCollection.value,
        'estimate'
      );
    } else {
      estimateSum = items.value.reduce((acc, item) => acc + item.estimate, 0);
    }
    return estimateString(estimateSum, {
      showUnitOfTime: true,
      minutesIfLessThanHour: !!estimateSum,
      spaceBetweenNumberAndUnitOfTime: true,
    });
  });

  const totalTimeEstimateSd = computed(() => {
    let estimateSdSum = 0;
    if (showTotalsBasedOnAssignee.value) {
      estimateSdSum = getAggregatedCountBasedOnAssigneeFilter(
        'estimateSd',
        filters.value?.assigneeUsername || []
      );
    } else if (aggregationsCollection?.value) {
      estimateSdSum = getAggregationCountBasedOnAttr(
        aggregationsCollection.value,
        'estimateSD'
      );
    } else {
      estimateSdSum = items.value.reduce(
        (acc, item) => acc + item.estimateSd,
        0
      );
    }
    return estimateString(estimateSdSum);
  });

  const totalPointEstimate = computed(() => {
    if (showTotalsBasedOnAssignee.value) {
      return getAggregatedCountBasedOnAssigneeFilter(
        'pointEffort',
        filters.value?.assigneeUsername || []
      );
    }
    if (aggregationsCollection?.value) {
      return getAggregationCountBasedOnAttr(
        aggregationsCollection.value,
        'pointEffort'
      );
    }
    const filteredPointEstimateSum = items.value.reduce(
      (acc, item) => acc + item.pointEffort,
      0
    );
    return filteredPointEstimateSum;
  });

  const totalTimeSpentSeconds = computed(() => {
    if (showTotalsBasedOnAssignee.value) {
      return getAggregatedCountBasedOnAssigneeFilter(
        'timeSpentSeconds',
        filters.value?.assigneeUsername || []
      );
    }

    if (aggregationsCollection?.value) {
      return getAggregationCountBasedOnAttr(
        aggregationsCollection.value,
        'timeSpentSeconds'
      );
    }

    const filteredTimeSpentSum = items.value.reduce((acc, item) => {
      if (item.timeSpentSeconds !== undefined) {
        return acc + item.timeSpentSeconds;
      }
      return acc;
    }, 0);
    return filteredTimeSpentSum;
  });

  const allAssigneeUsernames = computed(() => {
    const assigneeIds: string[] = [];
    aggregationsCollection?.value?.forEach((aggregation) => {
      if (aggregation.assigneeUsernames) {
        assigneeIds.push(...(aggregation.assigneeUsernames as string[]));
      }
    });
    return uniq(assigneeIds);
  });

  return {
    showTotalsBasedOnAssignee,
    getAggregationCountBasedOnAttr,
    getAggregatedCountBasedOnAssigneeFilter,

    totalItemCount,
    totalMemberCount,
    totalTimeEstimate,
    totalTimeEstimateSd,
    totalPointEstimate,
    totalTimeSpentSeconds,
    allAssigneeUsernames,
  };
}

// Board Folder hierarchy
export function useFetchBoardEntities(exclude?: Array<'boards' | 'cycles'>) {
  const boardStore = useBoardStore();

  const initialLoadingState = () => ({
    /** this is the only blocking load, others are non-blocking */
    folder: false,
    folders: false,
    boards: false,
    cycles: false,
    recents: false,
  });
  const isLoading = ref(initialLoadingState());

  async function loadIndexPage() {
    isLoading.value.folders = true;
    await boardStore.getBoardFolders({ parentId: undefined });
    boardStore.addAlreadyLoadedFolderId('root', { folders: true });
    isLoading.value.folders = false;
  }

  async function loadFolderPage(
    folderId: BoardFolder['id'],
    searchQueryParam?: string
  ) {
    boardStore.setSelectorFolderId(folderId);

    // Update search state for folder search
    searchQuery.value = searchQueryParam || '';
    isSearching.value = !!searchQueryParam;

    // Debug logging
    if (searchQueryParam) {
      // Search query is being used
    }

    isLoading.value.folder = true;
    isLoading.value.folders = true;

    const loadTasks = [];

    if (!exclude || !exclude.includes('boards')) {
      isLoading.value.boards = true;
    }
    if (!exclude || !exclude.includes('cycles')) {
      isLoading.value.cycles = true;
    }

    // load folder (blocking, unless already loaded)
    await (async () => {
      const folder = boardStore.selectorFolder;
      const p = boardStore.getBoardFolder(folderId);
      p.then(() => {
        isLoading.value.folder = false;
      });
      // don't block if folder is already loaded
      if (folder && folder.id === folderId) return;
      else await p;
    })();
    const folder = boardStore.selectorFolder;
    if (!folder) {
      // Folder not found - reset loading state
      isLoading.value = initialLoadingState();
      return;
    }

    // load boards (non-blocking)
    if (!exclude || !exclude.includes('boards')) {
      loadTasks.push(
        (async () => {
          await (folder.canHaveBoards
            ? boardStore.getBoards({
                boardFolderId: folderId,
                timeBased: false,
                labelContains: searchQueryParam,
              })
            : Promise.resolve());
          boardStore.addAlreadyLoadedFolderId(folderId, { boards: true });
          isLoading.value.boards = false;
        })()
      );
    }

    // load cycles (non-blocking)
    if (!exclude || !exclude.includes('cycles')) {
      loadTasks.push(
        (async () => {
          await (folder.canHaveCycles
            ? boardStore.getBoards({
                boardFolderId: folderId,
                timeBased: true,
                labelContains: searchQueryParam,
              })
            : Promise.resolve());
          boardStore.addAlreadyLoadedFolderId(folderId, { cycles: true });
          isLoading.value.cycles = false;
        })()
      );
    }

    // load folders (non-blocking)
    loadTasks.push(
      (async () => {
        await boardStore.getBoardFolders({ parentId: folderId });
        boardStore.addAlreadyLoadedFolderId(folderId, { folders: true });
        isLoading.value.folders = false;
      })()
    );

    await Promise.all(loadTasks);
  }
  async function loadPage(
    folderId?: BoardFolder['id'],
    searchQueryParam?: string
  ) {
    boardStore.setSelectorFolderId(undefined);

    // Update search state
    searchQuery.value = searchQueryParam || '';
    isSearching.value = !!searchQueryParam;

    if (!folderId) {
      if (searchQueryParam) {
        // For root level search, load boards with search query
        isLoading.value.boards = true;
        await boardStore.getBoards({
          labelContains: searchQueryParam,
          limit: 100,
        });
        isLoading.value.boards = false;
      } else {
        isSearching.value = false; // Clear search mode when loading index
        await loadIndexPage();
      }
    } else {
      await loadFolderPage(folderId, searchQueryParam);
    }
  }

  // Track if we're in search mode
  const isSearching = ref(false);
  const searchQuery = ref('');

  const current = computed(() => {
    // When searching at root level, return all boards from the store
    if (isSearching.value && !boardStore.selectorFolder) {
      const result = {
        folder: boardStore.selectorFolder,
        rootFolders: boardStore.rootFolders,
        folders: [],
        boards: boardStore.boards, // All boards from store when searching at root
        cycles: boardStore.cycles, // All cycles from store when searching at root
      };

      // Search results computed without logging

      return result;
    }

    // Normal folder-based view
    return {
      folder: boardStore.selectorFolder,
      rootFolders: boardStore.rootFolders,
      folders: boardStore.selectorFolder?.folders || [],
      boards: boardStore.selectorFolder?.boards || [],
      cycles: boardStore.selectorFolder?.cycles || [],
    };
  });

  const {
    recents: unloadedRecents,
    loadedRecents,
    getLoadedRecents,
  } = useMasterSearchRecents({
    useAutomaticSyncing: true,
    resultTypes: ['board', 'cycle'],
  });

  const recent = computed(() => {
    const hasRecentBoards = unloadedRecents.value.some(
      (r) => r.resultType === 'board'
    );
    const hasRecentCycles = unloadedRecents.value.some(
      (r) => r.resultType === 'cycle'
    );
    const hasRecents = hasRecentBoards || hasRecentCycles;
    const recentBoards = loadedRecents.value
      .filter((r) => r.type === 'board')
      .map((r) => r.entity as Board);
    const recentCycles = loadedRecents.value
      .filter((r) => r.type === 'cycle')
      .map((r) => r.entity as Board);
    const recentBoardsAndCycles = loadedRecents.value.map(
      (r) => r.entity as Board
    );
    return {
      hasRecentBoards,
      hasRecentCycles,
      hasRecents,

      recentBoards,
      recentCycles,
      recentBoardsAndCycles,
    };
  });
  async function loadRecents() {
    if (!recent.value.hasRecents) return;
    isLoading.value.recents = true;
    await getLoadedRecents();
    isLoading.value.recents = false;
  }

  const isAnythingLoading = computed(() => {
    return Object.values(isLoading.value).some((v) => v);
  });

  return {
    loadPage,
    isLoading,
    current,

    recent,
    loadRecents,
    isAnythingLoading,
  };
}

export function boardLabelWithFirstParent(board?: Board) {
  if (!board) return '';
  const parentLabel = board.hierarchy[board.hierarchy.length - 2]?.label;
  return parentLabel ? `${board.label} - ${parentLabel}` : board.label;
}

export function isArchivedBoardOrFolder(boardOrFolder: BoardFolder | Board) {
  return (
    boardOrFolder.archived || !!boardOrFolder.hierarchy.some((h) => h.archived)
  );
}

export function getBoardEntityProvider(
  boardOrFolder: BoardFolder | Board
): IntegrationProvider | null {
  if (boardOrFolder.integrations?.ado) return IntegrationProvider.ADO;
  if (boardOrFolder.integrations?.jira) return IntegrationProvider.JIRA;
  return null;
}

export const getBoardNamespacedLocalStorageKey = (
  localStorageKey: LocalStorageKeys,
  board: Board | undefined
): string => {
  const key = orgScopedKey(localStorageKey);
  if (!board) return key;
  // all cycles within the same folder should be namespaced together
  if (board.timeBased) return `${key}-${board.boardFolderId}`;
  // all boards should have their own namespace
  return `${key}-${board.id}`;
};

export type StoredLaneStates = Record<
  Lane['id'],
  {
    width: number;
    collapsed: boolean;
  }
>;

export function useStoredLaneStates(
  props: {
    /** if provided, `storedSize` will be based on this laneId. default width will be used if the key is not found in local storage */
    laneId?: ComputedRef<Lane['id'] | undefined>;
    /**
     * if provided, `storedLaneStates` will be based on the lanes within this board or cycle.
     * Lane state will also be stored namespaced by this board or cycle id.
     */
    board?: ComputedRef<Board | undefined>;
  } = {}
) {
  const COLUMN_MIN_WIDTH_PX = 150;
  const COLUMN_COMPACT_MIN_WIDTH_PX = 310;
  const COLUMN_DEFAULT_WIDTH_PX = 390;

  const laneWidths = (() => {
    const ret = useStorageWithComputedKey<Record<Lane['id'], number>>(
      computed(() =>
        getBoardNamespacedLocalStorageKey(
          LocalStorageKeys.KANBAN_LANES_STORED_SIZES,
          props.board?.value
        )
      ),
      {}
    );
    // corruption check: make sure its an object, make sure values are numbers, delete if not
    if (!isPlainObject(ret.value)) ret.value = {};
    Object.entries(ret.value).forEach(([key, value]) => {
      if (typeof value !== 'number') delete ret.value[key];
    });

    return ret;
  })();

  const storedSize = (() => {
    const key = computed(() => props.laneId?.value || 'default');
    return computed({
      get: () => laneWidths.value[key.value] || COLUMN_DEFAULT_WIDTH_PX,
      set: (value: number) => {
        if (!props.laneId?.value) throw new Error('No laneId provided');
        laneWidths.value[key.value] = value;
      },
    });
  })();

  const DEFAULT_COLLAPSED_STATE = false;

  const laneCollapseStates = (() => {
    const ret = useStorageWithComputedKey<Record<Lane['id'], boolean>>(
      computed(() =>
        getBoardNamespacedLocalStorageKey(
          LocalStorageKeys.KANBAN_LANE_COLLAPSED,
          props.board?.value
        )
      ),
      {}
    );
    // corruption check: make sure its an object, make sure values are booleans, delete if not
    if (!isPlainObject(ret.value)) ret.value = {};
    Object.entries(ret.value).forEach(([key, value]) => {
      if (typeof value !== 'boolean') delete ret.value[key];
    });

    return ret;
  })();

  const storedCollapseState = (() => {
    const key = computed(() => props.laneId?.value || 'default');
    return computed({
      get: () => laneCollapseStates.value[key.value] || DEFAULT_COLLAPSED_STATE,
      set: (value: boolean) => {
        if (!props.laneId?.value) throw new Error('No laneId provided');
        laneCollapseStates.value[key.value] = value;
      },
    });
  })();

  const { allLanes: lanesToUse } = useLanes({
    boardIds: computed(() => {
      const boardId = props.board?.value?.id;
      return boardId ? [boardId] : [];
    }),
  });
  const storedLaneStates = computed<StoredLaneStates>(() => {
    const ret: Record<
      Lane['id'],
      {
        width: number;
        collapsed: boolean;
      }
    > = {};
    for (const lane of lanesToUse.value) {
      const laneId = lane.id;

      ret[laneId] = {
        width: laneWidths.value[laneId] || COLUMN_DEFAULT_WIDTH_PX,
        collapsed: laneCollapseStates.value[laneId] || DEFAULT_COLLAPSED_STATE,
      };
    }
    return ret;
  });

  const hasCustomizationsInStoredLaneStates = computed(() => {
    for (const lane of lanesToUse.value) {
      const laneId = lane.id;

      const width = laneWidths.value[laneId];
      if (width && width !== COLUMN_DEFAULT_WIDTH_PX) return true;

      const collapsed = laneCollapseStates.value[laneId];
      if (collapsed !== undefined && collapsed !== DEFAULT_COLLAPSED_STATE)
        return true;
    }
    return false;
  });

  function applyStoredLaneStates(laneStates: StoredLaneStates) {
    for (const [laneId, state] of Object.entries(laneStates)) {
      laneWidths.value[laneId] = state.width;
      laneCollapseStates.value[laneId] = state.collapsed;
    }
  }

  function clearStoredLaneStates() {
    for (const lane of lanesToUse.value) {
      const laneId = lane.id;
      delete laneWidths.value[laneId];
      delete laneCollapseStates.value[laneId];
    }
  }

  return {
    COLUMN_MIN_WIDTH_PX,
    COLUMN_COMPACT_MIN_WIDTH_PX,
    COLUMN_DEFAULT_WIDTH_PX,
    storedSize,
    storedCollapseState,
    storedLaneStates,
    hasCustomizationsInStoredLaneStates,
    applyStoredLaneStates,
    clearStoredLaneStates,
  };
}

export function getCadenceProvider(
  cadence: Cadence
): IntegrationProvider | null {
  if (cadence.integrations?.ado) return IntegrationProvider.ADO;
  if (cadence.integrations?.jira) return IntegrationProvider.JIRA;
  return null;
}
